import { FC, useRef, useEffect } from 'react'
import { TSpeechToTextProps } from './SpeechToText.types'
import styles from './SpeechToText.module.scss'
import { Button } from '@mui/material'
import MicIcon from '@mui/icons-material/Mic'
import { useOrganizationSettings } from 'lib/context/OrganizationSettingsContext'
import { useTranscription } from 'hooks/useTranscription'
import { useGetTranscribeCredentials } from 'lib/services/restApi/transcribe/transcribe'

const SpeechToText: FC<TSpeechToTextProps> = ({
  onTranscriptChange,
  originalText,
}) => {
  const { enableDictation } = useOrganizationSettings()
  const {
    data: temporaryCredentials,
    isLoading: isLoadingCredentials,
    refetch: refetchCredentials,
  } = useGetTranscribeCredentials()
  const {
    transcript,
    isRecording,
    isProcessing,
    error,
    startRecording,
    stopRecording,
    reset,
  } = useTranscription({
    specialty: 'PRIMARYCARE',
    temporaryCredentials: temporaryCredentials?.data,
  })

  const lastProcessedTranscriptRef = useRef('')

  // Preload credentials when component mounts
  useEffect(() => {
    refetchCredentials()
  }, [refetchCredentials])

  // Cleanup effect to stop recording when component unmounts
  useEffect(() => {
    return () => {
      if (isRecording) {
        stopRecording()
        reset()
      }
    }
  }, [isRecording, stopRecording, reset])

  // Handle transcript changes
  useEffect(() => {
    if (transcript && transcript !== lastProcessedTranscriptRef.current) {
      const newText = originalText
        ? `${originalText}${originalText.endsWith(' ') ? '' : ' '}${transcript}`
        : transcript
      onTranscriptChange(newText)
      lastProcessedTranscriptRef.current = transcript
    }
  }, [transcript, originalText, onTranscriptChange])

  const handleStartListening = async () => {
    if (!isRecording) {
      lastProcessedTranscriptRef.current = ''
      try {
        await startRecording()
      } catch (err) {
        console.error('Failed to start recording:', err)
      }
    } else {
      stopRecording()
    }
  }

  if (!enableDictation) {
    return null
  }

  return (
    <div className={styles.speechToTextContainer}>
      <Button
        className={styles.speechToTextButton}
        onClick={handleStartListening}
        disabled={isProcessing || isLoadingCredentials}
      >
        <MicIcon color={isRecording ? 'success' : 'primary'} />
        <span
          style={{ color: isRecording ? 'var(--success)' : 'var(--primary)' }}
        >
          {isLoadingCredentials
            ? 'Loading...'
            : isProcessing
            ? 'Starting...'
            : isRecording
            ? 'Stop Recording'
            : 'Start Speaking'}
        </span>
      </Button>
      {error && <div className={styles.error}>{error}</div>}
    </div>
  )
}

export default SpeechToText
